import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MapPin, Scale, FileCheck } from "lucide-react";

const COMMON = ["reference_number", "candidate_name", "date_of_birth", "country"];

function CommonFields({ form, upd }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="reference_number">Reference number</Label>
        <Input id="reference_number" required value={form.reference_number} onChange={upd("reference_number")} data-testid="check-ref-input" className="mt-1.5" />
      </div>
      <div>
        <Label htmlFor="candidate_name">Candidate full name</Label>
        <Input id="candidate_name" required value={form.candidate_name} onChange={upd("candidate_name")} data-testid="check-candidate-input" className="mt-1.5" />
      </div>
      <div>
        <Label htmlFor="date_of_birth">Date of birth</Label>
        <Input id="date_of_birth" type="date" required value={form.date_of_birth} onChange={upd("date_of_birth")} data-testid="check-dob-input" className="mt-1.5" />
      </div>
      <div>
        <Label htmlFor="country">Country</Label>
        <Input id="country" required value={form.country} onChange={upd("country")} data-testid="check-country-input" className="mt-1.5" />
      </div>
    </div>
  );
}

export default function NewCheckPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("address");
  const [form, setForm] = useState({
    reference_number: "", candidate_name: "", date_of_birth: "", country: "",
    full_address: "", litigation_type: "", id_type: "", id_number: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { check_type: tab };
    for (const k of COMMON) payload[k] = form[k];
    if (tab === "address") payload.full_address = form.full_address;
    if (tab === "litigation") { payload.full_address = form.full_address; payload.litigation_type = form.litigation_type; }
    if (tab === "id") { payload.id_type = form.id_type; payload.id_number = form.id_number; }
    try {
      await api.post("/checks", payload);
      toast.success("Verification request submitted");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900">Submit a new check</h1>
      <p className="text-slate-600 mt-1 text-sm">Choose a check type — we handle the rest.</p>

      <div className="mt-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 p-1 h-auto">
            <TabsTrigger value="address" data-testid="tab-address" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 gap-2 px-4 py-2">
              <MapPin size={14} /> Address
            </TabsTrigger>
            <TabsTrigger value="litigation" data-testid="tab-litigation" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 gap-2 px-4 py-2">
              <Scale size={14} /> Litigation
            </TabsTrigger>
            <TabsTrigger value="id" data-testid="tab-id" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 gap-2 px-4 py-2">
              <FileCheck size={14} /> ID
            </TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="mt-6 border border-slate-200 bg-white rounded-2xl p-7 space-y-5" data-testid="new-check-form">
            <CommonFields form={form} upd={upd} />

            <TabsContent value="address" className="m-0 space-y-4">
              <div>
                <Label htmlFor="full_address">Full address</Label>
                <Textarea id="full_address" required={tab === "address"} rows={3} value={form.full_address} onChange={upd("full_address")} data-testid="check-address-input" className="mt-1.5" />
              </div>
            </TabsContent>

            <TabsContent value="litigation" className="m-0 space-y-4">
              <div>
                <Label htmlFor="full_address_l">Full address</Label>
                <Textarea id="full_address_l" required={tab === "litigation"} rows={3} value={form.full_address} onChange={upd("full_address")} data-testid="check-lit-address-input" className="mt-1.5" />
              </div>
              <div>
                <Label>Type of litigation</Label>
                <Select value={form.litigation_type} onValueChange={(v) => setForm((f) => ({ ...f, litigation_type: v }))}>
                  <SelectTrigger data-testid="check-lit-type-select" className="mt-1.5"><SelectValue placeholder="Select litigation type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Civil">Civil</SelectItem>
                    <SelectItem value="Criminal">Criminal</SelectItem>
                    <SelectItem value="Civil+Criminal">Civil + Criminal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="id" className="m-0 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="id_type">Type of ID</Label>
                  <Input id="id_type" required={tab === "id"} placeholder="Passport, National ID, Driving License…" value={form.id_type} onChange={upd("id_type")} data-testid="check-idtype-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="id_number">ID number</Label>
                  <Input id="id_number" required={tab === "id"} value={form.id_number} onChange={upd("id_number")} data-testid="check-idnumber-input" className="mt-1.5" />
                </div>
              </div>
            </TabsContent>

            <div className="pt-2 flex gap-3">
              <Button type="submit" disabled={busy} data-testid="submit-check-btn" className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11">
                {busy ? "Submitting…" : "Submit check"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full">Cancel</Button>
            </div>
          </form>
        </Tabs>
      </div>
    </div>
  );
}
